import { Button, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { ButtonV2, useBreakpoint } from '@eppendorf/vnls-react-components-preview';
import React, { ReactElement, useEffect } from 'react';

import { PageListProps } from './pagination.definitions';
import { dots, usePagination } from './usePagination';

export function PageList({
  onPageChange,
  totalPages,
  pageSiblingCount,
  currentPage,
  className,
}: PageListProps): ReactElement | null {
  const isMinSm = useBreakpoint({ min: 'sm' });

  const { paginationRange, activePage, setActivePage } = usePagination({
    currentPage,
    totalPages,
    pageSiblingCount,
  });

  useEffect(() => {
    if (
      paginationRange &&
      currentPage > (paginationRange?.[paginationRange.length - 1] as number)
    ) {
      setActivePage(1);
      onPageChange(1);
    }
  }, [paginationRange, currentPage, onPageChange]);

  function handleArrowClick(direction: 'prev' | 'next'): void {
    if (direction === 'prev') {
      onPageChange(currentPage - 1);
      setActivePage(currentPage - 1);
    } else {
      onPageChange(currentPage + 1);
      setActivePage(currentPage + 1);
    }
  }

  function onLeftDotsClick(): void {
    if (currentPage === paginationRange?.[paginationRange.length - 1]) {
      setActivePage(activePage - 4);
    } else {
      setActivePage(activePage - 3);
    }
  }

  function onRightDotsClick(): void {
    if (currentPage === 1) {
      setActivePage(activePage + 4);
    } else {
      setActivePage(activePage + 3);
    }
  }

  if (!isMinSm) {
    return (
      <ul className={className ?? 'pagination__pagelist'}>
        <li>
          <ButtonV2
            variant="outline"
            onPress={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-left-s"
          >
            <Icon
              name="left"
              disabled={currentPage === 1}
              size={IconSizeClasses.XSmall}
            />
            Previous
          </ButtonV2>
        </li>

        <li className="flex flex__ai--center">
          <span className="p-left-l p-right-l font-size-l">
            {currentPage}/{totalPages}
          </span>
        </li>

        <li>
          <ButtonV2
            variant="outline"
            size="md"
            onPress={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-right-s"
          >
            <span>{'\u00A0\u00A0Next\u00A0\u00A0'}</span>
            <Icon
              name="right"
              disabled={currentPage === totalPages}
              size={IconSizeClasses.XSmall}
            />
          </ButtonV2>
        </li>
      </ul>
    );
  }

  return (
    <ul className={className ?? 'pagination__pagelist'}>
      <li>
        <Button
          onClick={() => handleArrowClick('prev')}
          disabled={currentPage === 1}
          variant="tertiary"
        >
          <Icon name="left" disabled={currentPage === 1} size={IconSizeClasses.Small} />
        </Button>
      </li>

      {paginationRange?.map((pageNumber, i, src) => {
        if (pageNumber === dots) {
          const middleIndex = Math.floor(src.length / 2);
          return (
            <li key={`dots${i > middleIndex ? 'right' : 'left'}`}>
              <Button
                variant="secondary"
                onClick={i > middleIndex ? onRightDotsClick : onLeftDotsClick}
              >
                ...
              </Button>
            </li>
          );
        }

        return (
          <li key={`page-${pageNumber}`}>
            <Button
              variant={pageNumber === currentPage ? 'primary' : 'secondary'}
              onClick={() => {
                onPageChange(pageNumber);
                setActivePage(pageNumber);
              }}
            >
              {pageNumber}
            </Button>
          </li>
        );
      })}

      <li>
        <Button
          onClick={() => handleArrowClick('next')}
          disabled={currentPage === paginationRange?.[paginationRange.length - 1]}
          variant="tertiary"
        >
          <Icon
            name="right"
            disabled={currentPage === paginationRange?.[paginationRange.length - 1]}
            size={IconSizeClasses.Small}
          />
        </Button>
      </li>
    </ul>
  );
}
